import {
  createSolanaRpc,
  getBase64Decoder,
  getBase64Encoder,
  getCompiledTransactionMessageDecoder,
  getTransactionDecoder,
  getTransactionEncoder,
  type Base64EncodedWireTransaction,
  type Transaction,
} from "@solana/kit";
import { rpcUrl } from "../env";

export function base64ToBytes(b64: string): Uint8Array {
  return new Uint8Array(getBase64Encoder().encode(b64));
}

export function bytesToBase64(bytes: Uint8Array): string {
  return getBase64Decoder().decode(bytes);
}

export function decodeWireTransaction(bytes: Uint8Array): Transaction {
  return getTransactionDecoder().decode(bytes);
}

export function encodeWireTransaction(tx: Transaction): Uint8Array {
  return new Uint8Array(getTransactionEncoder().encode(tx));
}

/** Fee payer = first static account of the compiled message (legacy and v0 alike). */
export function feePayerOf(tx: Transaction): string {
  const message = getCompiledTransactionMessageDecoder().decode(tx.messageBytes);
  const payer = message.staticAccounts[0];
  if (!payer) throw new Error("Transaction has no accounts");
  return payer;
}

export function requiredSigners(tx: Transaction): string[] {
  return Object.keys(tx.signatures);
}

function sameBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/**
 * Some wallets return only their own signature. Keep every signature the API already put
 * on the transaction (GA or Kora partial signature) and add the wallet's.
 * Refuses a wallet that changed the message.
 */
export function mergeSignatures(original: Uint8Array, signedByWallet: Uint8Array): Uint8Array {
  const before = decodeWireTransaction(original);
  const after = decodeWireTransaction(signedByWallet);
  if (!sameBytes(new Uint8Array(before.messageBytes), new Uint8Array(after.messageBytes))) {
    throw new Error("The wallet changed the transaction before signing; refusing to send it.");
  }
  const signatures = { ...before.signatures };
  for (const [addr, sig] of Object.entries(after.signatures)) {
    if (sig) (signatures as Record<string, typeof sig>)[addr] = sig;
  }
  return encodeWireTransaction({ ...before, signatures } as Transaction);
}

let rpc: ReturnType<typeof createSolanaRpc> | null = null;

/** Sends a fully signed transaction whose fee payer is the player's own wallet. */
export async function sendSignedTransaction(bytes: Uint8Array): Promise<string> {
  rpc ??= createSolanaRpc(rpcUrl);
  const wire = bytesToBase64(bytes) as Base64EncodedWireTransaction;
  return rpc.sendTransaction(wire, { encoding: "base64", preflightCommitment: "confirmed" }).send();
}
